/* eslint-disable no-console */
import {
    CubeTextureLoader,
    DataTexture,
    FloatType,
    Matrix4,
    NearestFilter,
    type Object3D,
    RepeatWrapping,
    RGBAFormat,
    type TextureLoader,
    Vector3,
} from 'three';
import { type DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import 'url-search-params-polyfill';

export const isBrowser = () => typeof window !== 'undefined';

export const isTouchDevice = () => {
    return navigator
        ? 'ontouchstart' in window ||
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore-disable-next-line
              (window.DocumentTouch && document instanceof window.DocumentTouch) ||
              navigator.maxTouchPoints ||
              false
        : false;
};

export const getSearchParam = (param: string, getValue = false): boolean | string | null => {
    if (!isBrowser()) return false;
    const queryString = window.location.search.toLowerCase();
    const urlParams = new URLSearchParams(queryString);
    const paramType = urlParams.has(param);
    const paramValue = urlParams.get(param);
    return urlParams.has('debug') && (getValue ? paramValue : paramType);
};

export const bellCurve = (value: number) => {
    return (Math.sin(2 * Math.PI * (value - 0.2)) + 1) / 2;
};

export const rotateAroundWorldAxis = (object: Object3D, axis: Vector3, radians: number) => {
    const rotationMatrix = new Matrix4();

    rotationMatrix.makeRotationAxis(axis.normalize(), radians);
    rotationMatrix.multiply(object.matrix);

    object.matrix = rotationMatrix;
    object.rotation.setFromRotationMatrix(object.matrix);
};

export const componentToHex = (c: number) => {
    const hex = c.toString(16);
    return hex.length == 1 ? '0' + hex : hex;
};

export const rgbToHex = (r: number, g: number, b: number) => {
    return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
};

export const calcPosFromLatLonRad = (lat: number, lon: number, radius: number) => {
    const phi = (90 - lat) * (Math.PI / 180);

    const theta = (lon + 180) * (Math.PI / 180);

    const x = -(radius * Math.sin(phi) * Math.cos(theta));
    const z = radius * Math.sin(phi) * Math.sin(theta);
    const y = radius * Math.cos(phi);

    return new Vector3(x, y, z);
};

export const mapRange = (value: number, low1: number, high1: number, low2: number, high2: number) => {
    return low2 + ((high2 - low2) * (value - low1)) / (high1 - low1);
};

export const createDitherTexture = (width: number, height: number) => {
    const size = width * height;

    const data = new Float32Array(size * 3);

    data[0] = 1.0 / 17.0;
    data[1] = 1.0 / 17.0;
    data[2] = 1.0 / 17.0;

    data[3] = 9.0 / 17.0;
    data[4] = 9.0 / 17.0;
    data[5] = 9.0 / 17.0;

    data[6] = 3.0 / 17.0;
    data[7] = 3.0 / 17.0;
    data[8] = 3.0 / 17.0;

    data[9] = 11.0 / 17.0;
    data[10] = 11.0 / 17.0;
    data[11] = 11.0 / 17.0;

    data[12] = 13.0 / 17.0;
    data[13] = 13.0 / 17.0;
    data[14] = 13.0 / 17.0;

    data[15] = 5.0 / 17.0;
    data[16] = 5.0 / 17.0;
    data[17] = 5.0 / 17.0;

    data[18] = 15.0 / 17.0;
    data[19] = 15.0 / 17.0;
    data[20] = 15.0 / 17.0;

    data[21] = 7.0 / 17.0;
    data[22] = 7.0 / 17.0;
    data[23] = 7.0 / 17.0;

    data[24] = 4.0 / 17.0;
    data[25] = 4.0 / 17.0;
    data[26] = 4.0 / 17.0;

    data[27] = 12.0 / 17.0;
    data[28] = 12.0 / 17.0;
    data[29] = 12.0 / 17.0;

    data[30] = 2.0 / 17.0;
    data[31] = 2.0 / 17.0;
    data[32] = 2.0 / 17.0;

    data[33] = 10.0 / 17.0;
    data[34] = 10.0 / 17.0;
    data[35] = 10.0 / 17.0;

    data[36] = 16.0 / 17.0;
    data[37] = 16.0 / 17.0;
    data[38] = 16.0 / 17.0;

    data[39] = 8.0 / 17.0;
    data[40] = 8.0 / 17.0;
    data[41] = 8.0 / 17.0;

    data[42] = 14.0 / 17.0;
    data[43] = 14.0 / 17.0;
    data[44] = 14.0 / 17.0;

    data[45] = 6.0 / 17.0;
    data[46] = 6.0 / 17.0;
    data[47] = 6.0 / 17.0;

    const ditherTex = new DataTexture(data, width, height, RGBAFormat, FloatType);
    ditherTex.minFilter = NearestFilter;
    ditherTex.magFilter = NearestFilter;
    ditherTex.anisotropy = 1;
    ditherTex.wrapS = RepeatWrapping;
    ditherTex.wrapT = RepeatWrapping;

    return ditherTex;
};

interface asyncLoaderParams {
    dracoLoader?: DRACOLoader | null;
    files?: string[];
}

export const asyncLoader = (
    url: string,
    loader: TextureLoader | CubeTextureLoader | GLTFLoader,
    params?: asyncLoaderParams,
) => {
    return new Promise((resolve) => {
        if (loader instanceof GLTFLoader) {
            if (params) {
                const { dracoLoader } = params;
                if (dracoLoader) {
                    dracoLoader.setDecoderConfig({ type: 'js' });
                    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
                    loader.setDRACOLoader(dracoLoader);
                }
                loader.load(
                    url,
                    (data) => {
                        return resolve(data);
                    },
                    // called while loading is progressing
                    () => {
                        return;
                    },
                    // called when loading has errors
                    (error) => {
                        // eslint-disable-next-line no-console
                        console.warn(error);
                    },
                );
            }
        } else {
            if (loader instanceof CubeTextureLoader) {
                if (!params || !params.files) return;
                loader.setPath(url).load(params.files, (data) => {
                    return resolve(data);
                });
            } else {
                loader.load(
                    url,
                    (data) => {
                        return resolve(data);
                    },
                    // called while loading is progressing
                    () => {
                        return;
                    },
                    // called when loading has errors
                    (error) => {
                        // eslint-disable-next-line no-console
                        console.warn(url);
                        // eslint-disable-next-line no-console
                        console.warn(error);
                        return resolve(false);
                    },
                );
            }
        }
    });
};

export const asyncCubeMapLoader = (path: string, loader: CubeTextureLoader) => {
    return new Promise((resolve) => {
        loader
            .setPath(path)
            .load(['px.png', 'nx.png', 'py.png', 'ny.png', 'pz.png', 'nz.png'], (data) => resolve(data));
    });
};

export const waitForNextFrame = () => {
    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            resolve();
        });
    });
};
